import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import styles from './AdminLayout.module.css';
import '../styles/admin.css';

/**
 * Единый каркас админки.
 *
 * Раньше блок навигации был скопирован в каждый из двенадцати экранов, в четырёх
 * несовместимых вариантах, а активный пункт задавался вручную через variant="dark"
 * или disabled — и местами указывал не на ту страницу. Здесь навигация одна,
 * а активный пункт определяется маршрутом.
 */

const NAV = [
    {
        items: [{ to: '/admin', label: 'Обзор', exact: true, icon: '◧' }],
    },
    {
        title: 'Научный журнал',
        items: [
            {
                to: '/admin/articles',
                label: 'Статьи',
                icon: '📄',
                children: [
                    { to: '/admin/dictionaries/authors', label: 'Авторы статей' },
                    { to: '/admin/dictionaries/keywords', label: 'Ключевые слова' },
                ],
            },
            { to: '/admin/journals', label: 'Журналы', icon: '📚' },
            { to: '/admin/releases', label: 'Выпуски', icon: '🗂' },
        ],
    },
    {
        title: 'Фонд',
        items: [
            {
                to: '/admin/educational',
                label: 'Учебные издания',
                icon: '📘',
                children: [
                    { to: '/admin/dictionaries/ed-authors', label: 'Авторы изданий' },
                    { to: '/admin/dictionaries/ed-categories', label: 'Направления изданий' },
                ],
            },
            {
                to: '/admin/disertations',
                label: 'Диссертации',
                icon: '🎓',
                children: [
                    { to: '/admin/dictionaries/dis-authors', label: 'Авторы диссертаций' },
                    { to: '/admin/dictionaries/dis-napravlenie', label: 'Направления диссертаций' },
                    { to: '/admin/dictionaries/dis-spec', label: 'Специальности' },
                ],
            },
        ],
    },
];

const AdminLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const { confirm } = useUi();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    // На мобильных меню перекрывает контент — после перехода его надо закрыть.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        const ok = await confirm({
            title: 'Выйти из учётной записи?',
            message: 'Несохранённые изменения в открытых формах будут потеряны.',
            confirmLabel: 'Выйти',
            danger: true,
        });
        if (ok) logout();
    };

    return (
        <div className={`adm-root ${styles.layout}`}>
            <header className={styles.topbar}>
                <button
                    type="button"
                    className={styles.burger}
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Меню разделов"
                    aria-expanded={menuOpen}
                >
                    <span />
                    <span />
                    <span />
                </button>

                <NavLink to="/admin" className={styles.brand}>
                    Электронная библиотека <span className={styles.brandMuted}>· админка</span>
                </NavLink>

                <div className={styles.topbarRight}>
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.siteLink}
                        title="Открыть сайт в новой вкладке"
                    >
                        Сайт ↗
                    </a>
                    <span className={styles.user} title={user || ''}>
                        {user || '—'}
                    </span>
                    <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={handleLogout}>
                        Выйти
                    </button>
                </div>
            </header>

            <div className={styles.body}>
                {menuOpen && <div className={styles.overlay} onClick={() => setMenuOpen(false)} />}

                <nav className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
                    {NAV.map((group, i) => (
                        <div key={group.title || i} className={styles.navGroup}>
                            {group.title && <div className={styles.navGroupTitle}>{group.title}</div>}
                            {group.items.map((item) => (
                                <div key={item.to} className={styles.navItem}>
                                    <NavLink
                                        to={item.to}
                                        exact={item.exact}
                                        className={styles.navLink}
                                        activeClassName={styles.navLinkActive}
                                    >
                                        {item.icon && <span className={styles.navIcon}>{item.icon}</span>}
                                        <span>{item.label}</span>
                                    </NavLink>

                                    {/* Справочники стоят под своим разделом, а не отдельным
                                        списком в конце меню: искать «Авторы статей» логичнее
                                        рядом со «Статьями», а не через всё меню ниже. */}
                                    {item.children && (
                                        <div className={styles.navChildren}>
                                            {item.children.map((child) => (
                                                <NavLink
                                                    key={child.to}
                                                    to={child.to}
                                                    className={`${styles.navLink} ${styles.navChildLink}`}
                                                    activeClassName={styles.navLinkActive}
                                                >
                                                    <span>{child.label}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </nav>

                <main className={styles.content}>{children}</main>
            </div>
        </div>
    );
};

export default AdminLayout;
